import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtError } from "../api";
import { Inbox, RefreshCw, AlertTriangle, Paperclip, User, Clock, ArrowLeft, Send, Plus, HelpCircle, Pencil, X, Loader2, ClipboardList, CheckSquare, Square, MessageSquare, StickyNote, Trash2, Phone } from "lucide-react";
import { StatusBadge } from "./stoerung/StatusBadge";
import { AttachmentGallery } from "./stoerung/AttachmentGallery";
import { Timeline } from "./stoerung/Timeline";
import { getCachedMessages, setCachedMessages, updateCachedMessage, removeCachedMessage, getVerlaufSeen, markVerlaufSeen } from "../magMessages";

const ACTIONS = {
  accept: { label: "Annehmen", status: "in_progress", toast: "Vorgang angenommen – meldende Stelle informiert.", requiresText: false },
  question: { label: "Rückfrage", status: "in_progress", toast: "Rückfrage gesendet.", requiresText: true, placeholder: "Rückfrage an Standortleitung" },
  complete: { label: "Erledigt", status: "completed", toast: "Als erledigt markiert – meldende Stelle benachrichtigt.", requiresText: false },
  request_external_service: { label: "Fachfirma beauftragen", status: "external_service_required", toast: "An die meldende Stelle zur Fachfirma-Beauftragung übergeben.", requiresText: false },
  reopen: { label: "Ticket zurückgeben", status: "open", toast: "Ticket zurückgegeben – wieder offen.", requiresText: false },
  note: { label: "Notiz", status: undefined, toast: "Notiz gespeichert.", requiresText: true, placeholder: "Interne Notiz zu diesem Vorgang – wird im Verlauf gespeichert, benachrichtigt aber niemanden." },
  delete: { label: "Löschen", status: "deleted", toast: "Ticket gelöscht.", requiresText: false },
};
const ORDER = ["accept", "question", "complete", "request_external_service", "reopen", "note", "delete"];

const STATUS_PILLS = [
  { key: "open", label: "Offen" },
  { key: "in_progress", label: "Aufgabenliste" },
  { key: "completed", label: "Erledigt" },
  { key: "external_service_required", label: "Fachfirma" },
  { key: "all", label: "Alle" },
];
const RANGE_MS = { "7d": 7 * 86400000, "30d": 30 * 86400000 };

function fmtTime(iso) {
  try {
    return new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" }).format(new Date(iso));
  } catch { return iso || ""; }
}

// Rechtes WhatsApp-artiges Panel: Meldungen der gewählten Standort inkl.
// Inline-Bearbeitung (4 Aktionen) direkt unter jeder Meldung.
export function MeldungenPane({ permid, title, address, token, onBack, onReportIssue, initialFilter, onSeen }) {
  const [data, setData] = useState(() => getCachedMessages(permid)?.data || null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replyAction, setReplyAction] = useState("question");
  const [sendingId, setSendingId] = useState(null);
  const [sendingKey, setSendingKey] = useState(null);
  const [statusFilter, setStatusFilter] = useState(initialFilter === "open" ? "open" : "mine");
  const [rangeFilter, setRangeFilter] = useState("all");
  const [showDone, setShowDone] = useState(false);
  const [openTimeline, setOpenTimeline] = useState(() => new Set());
  const [seenMap, setSeenMap] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [callFor, setCallFor] = useState(null);
  const [callMgrs, setCallMgrs] = useState([]);
  const [callLoading, setCallLoading] = useState(false);
  const role = sessionStorage.getItem("mag_role") || "";
  const reportLabel = role === "janitor" ? "Neue Meldung" : "Hausmeisteranfrage";

  const fetchMessages = useCallback(async (force = false) => {
    if (!permid || !token) return;
    const cached = getCachedMessages(permid);
    const hdr = { headers: { Authorization: `Bearer ${token}` } };
    // Günstiger activity/subtree-Check: nur neu laden, wenn sich der Teilbaum geändert hat.
    let activity = null;
    try {
      const { data: act } = await api.get(`/magauth/activity/${permid}`, hdr);
      activity = act?.timestamp || null;
    } catch { /* Signatur nicht ermittelbar -> vollständig laden */ }
    if (!force && cached?.data && activity && cached.activity === activity) {
      setData(cached.data);
      return;
    }
    setLoading(true); setError("");
    try {
      const { data } = await api.get(`/magauth/messages/${permid}`, { ...hdr, params: { include_children: 1 } });
      setData(data); setCachedMessages(permid, data, activity);
    } catch (e) {
      setError(fmtError(e, "Meldungen konnten nicht geladen werden"));
      if (!cached?.data) setData(null);
    } finally {
      setLoading(false);
    }
  }, [permid, token]);

  useEffect(() => { fetchMessages(false); }, [fetchMessages]);

  useEffect(() => {
    const id = setInterval(() => fetchMessages(false), 30000);
    return () => clearInterval(id);
  }, [fetchMessages]);

  useEffect(() => { if (permid) onSeen?.(permid); }, [permid, onSeen]);

  const messages = data?.messages || [];
  const inRange = (iso) => {
    if (rangeFilter === "all") return true;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return false;
    if (rangeFilter === "today") {
      const n = new Date();
      return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
    }
    return d.getTime() >= Date.now() - RANGE_MS[rangeFilter];
  };
  const timeFiltered = messages.filter((m) => inRange(m.reportedAt));
  const me = (data?.me || "").toLowerCase();
  const involvesActive = (m) => !!me && ((m.acceptedBy || []).includes(me) || m.openQuestionBy === me);
  const involvesDone = (m) => !!me && ((m.acceptedBy || []).includes(me) || (m.completedBy || []).includes(me));
  const isMine = (m) => {
    if (!me) return false;
    if (m.status === "completed") return showDone && involvesDone(m);
    return involvesActive(m);
  };
  const counts = timeFiltered.reduce((a, m) => { a.all += 1; a[m.status] = (a[m.status] || 0) + 1; return a; }, { all: 0 });
  const mineCount = timeFiltered.filter(isMine).length;
  const visible = statusFilter === "mine"
    ? timeFiltered.filter(isMine)
    : (statusFilter === "all" ? timeFiltered : timeFiltered.filter((m) => m.status === statusFilter));

  const applyPatch = (id, patch) => {
    setData((prev) => (prev ? { ...prev, messages: prev.messages.map((m) => (String(m.id) === String(id) ? { ...m, ...patch } : m)) } : prev));
    updateCachedMessage(permid, { id, ...patch });
  };

  const doAction = async (m, action, message) => {
    const cfg = ACTIONS[action];
    if (cfg.requiresText && !message.trim()) return;
    if (!m.externalId) { toast.error("Diese Meldung ist unvollständig und kann nicht bearbeitet werden."); return; }
    setSendingId(m.id); setSendingKey(action);
    try {
      const payload = { action, message, permid, prop_title: title || "", issue_title: m.subject || m.title || "", issue_desc: m.description || "" };
      const { data: res } = await api.post(`/magauth/messages/${encodeURIComponent(m.externalId)}/action`, payload, { headers: { Authorization: `Bearer ${token}` } });
      if (action === "delete") {
        // Logisch gelöschtes Ticket aus Liste + Cache entfernen.
        setData((prev) => (prev ? { ...prev, messages: prev.messages.filter((x) => String(x.id) !== String(m.id)), count: Math.max(0, (prev.count || 1) - 1) } : prev));
        removeCachedMessage(permid, m.id);
        setConfirmDelete(null);
        toast.success(cfg.toast);
        return;
      }
      if (res && res.id) applyPatch(m.id, { ...res, id: m.id });
      else applyPatch(m.id, { status: (res && res.status) || cfg.status });
      if (action === "question" && res?.no_manager) {
        toast.warning("Manager für diesen Standort fehlt.");
      } else if (action === "question") {
        const notified = res?.managers_notified || 0;
        toast.success(notified > 0 ? `Rückfrage gesendet – ${notified} Manager benachrichtigt.` : "Rückfrage gesendet.");
      } else {
        toast.success(cfg.toast);
      }
      setEditId(null); setReplyFor(null); setReplyText(""); setReplyAction("question");
    } catch (e) {
      toast.error(fmtError(e, "Aktion konnte nicht gespeichert werden"));
    } finally {
      setSendingId(null); setSendingKey(null);
    }
  };

  const openCall = async (m) => {
    setCallFor(m.id); setReplyFor(null); setCallMgrs([]); setCallLoading(true);
    try {
      const { data } = await api.get(`/magauth/managers/${permid}`, { headers: { Authorization: `Bearer ${token}` } });
      setCallMgrs(data.managers || []);
    } catch { setCallMgrs([]); }
    finally { setCallLoading(false); }
  };
  const openEdit = (id) => { setEditId(id); setReplyFor(null); setCallFor(null); setReplyText(""); setReplyAction("question"); };
  const answerEntries = (m) => (m.timeline || []).filter((e) => e.authorType !== "reporter");
  const latestAnswerAt = (m) => answerEntries(m).reduce((mx, e) => ((e.createdAt || "") > mx ? e.createdAt : mx), "");
  const effectiveSeen = (m) => seenMap[m.id] ?? getVerlaufSeen(m.id) ?? "";
  const unreadCount = (m) => answerEntries(m).filter((e) => (e.createdAt || "") > effectiveSeen(m) && (e.byEmail || "") !== me).length;
  const toggleTimeline = (m) => {
    const opening = !openTimeline.has(m.id);
    setOpenTimeline((s) => { const n = new Set(s); if (n.has(m.id)) n.delete(m.id); else n.add(m.id); return n; });
    if (opening) {
      const latest = latestAnswerAt(m);
      markVerlaufSeen(m.id, latest);
      setSeenMap((p) => ({ ...p, [m.id]: latest }));
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="meldungen-pane">
      <div className="flex items-center gap-3 px-4 py-3" data-testid="chat-header" style={{ background: "#fff", borderBottom: "1px solid #E2E8F0" }}>
        <button className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" data-testid="chat-back-mobile" title="Zurück zur Standortübersicht"
          onClick={onBack} style={{ background: "#F1F5F9", border: "none", cursor: "pointer" }}><ArrowLeft size={16} /></button>
        <span className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "#FFF7ED" }}>
          <Inbox size={18} color="#F97316" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-extrabold text-sm truncate" data-testid="chat-title">{title}</div>
          <div className="text-xs truncate" style={{ color: "#64748B" }} data-testid="chat-subtitle">
            {loading && !data
              ? <span className="inline-flex items-center gap-1" data-testid="chat-init-loading"><Loader2 size={12} className="animate-spin" /> Wird geladen…</span>
              : (data ? `${data.count} Meldungen aus ${data.source_count} Objekten` : (address || `perm_id: ${permid}`))}
          </div>
        </div>
        <button className="btn-primary flex-shrink-0" data-testid="chat-report-issue" title={reportLabel}
          onClick={() => onReportIssue?.({ perm_id: permid, title })} style={{ padding: "9px 16px", fontSize: 14 }}>
          <Plus size={18} /> <span className="hidden sm:inline">{reportLabel}</span>
        </button>
        <button className="btn-secondary flex-shrink-0" data-testid="chat-refresh" onClick={() => fetchMessages(true)} disabled={loading} title="Neu laden" style={{ padding: "9px 11px" }}>
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="px-3 py-2" style={{ background: "#fff", borderBottom: "1px solid #E2E8F0" }} data-testid="meldungen-filter">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar" style={{ flex: 1, minWidth: 0 }}>
            {(() => {
              const active = statusFilter === "mine";
              return (
                <button data-testid="filter-status-mine" onClick={() => { setStatusFilter("mine"); fetchMessages(true); }}
                  className="inline-flex items-center gap-1.5 rounded-full font-bold flex-shrink-0"
                  style={{ border: `1px solid ${active ? "#047857" : "#E2E8F0"}`, background: active ? "#ECFDF5" : "#fff", color: active ? "#047857" : "#475569", cursor: "pointer", padding: "5px 12px", fontSize: 12 }}>
                  <ClipboardList size={13} /> Meine Aufgaben
                  <span className="rounded-full" style={{ background: active ? "#047857" : "#F1F5F9", color: active ? "#fff" : "#64748B", fontSize: 11, padding: "1px 7px" }}>{mineCount}</span>
                </button>
              );
            })()}
            {STATUS_PILLS.filter((p) => p.key === "all" || p.key === statusFilter || (counts[p.key] || 0) > 0).map((p) => {
              const active = statusFilter === p.key;
              const cnt = p.key === "all" ? counts.all : (counts[p.key] || 0);
              return (
                <button key={p.key} data-testid={`filter-status-${p.key}`} onClick={() => { setStatusFilter(p.key); fetchMessages(true); }}
                  className="inline-flex items-center gap-1.5 rounded-full font-bold flex-shrink-0"
                  style={{ border: `1px solid ${active ? "#F97316" : "#E2E8F0"}`, background: active ? "#FFF7ED" : "#fff", color: active ? "#C2410C" : "#475569", cursor: "pointer", padding: "5px 12px", fontSize: 12 }}>
                  {p.label}
                  <span className="rounded-full" style={{ background: active ? "#F97316" : "#F1F5F9", color: active ? "#fff" : "#64748B", fontSize: 11, padding: "1px 7px" }}>{cnt}</span>
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {statusFilter === "mine" && (
              <button data-testid="filter-mine-done-toggle" onClick={() => setShowDone((v) => !v)}
                className="inline-flex items-center gap-1.5 rounded-full font-semibold flex-shrink-0"
                style={{ border: `1px solid ${showDone ? "#047857" : "#E2E8F0"}`, background: showDone ? "#ECFDF5" : "#fff", color: showDone ? "#047857" : "#64748B", cursor: "pointer", padding: "6px 10px", fontSize: 12 }}>
                {showDone ? <CheckSquare size={14} /> : <Square size={14} />} Erledigte einblenden
              </button>
            )}
            <select data-testid="filter-range" className="input-wd" value={rangeFilter} onChange={(e) => setRangeFilter(e.target.value)}
              style={{ width: "auto", padding: "6px 10px", fontSize: 13, flexShrink: 0 }}>
              <option value="all">Gesamter Zeitraum</option>
              <option value="today">Heute</option>
              <option value="7d">Letzte 7 Tage</option>
              <option value="30d">Letzte 30 Tage</option>
            </select>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-4" style={{ background: "#F8FAFC" }}>
        {error && (
          <div className="card-wd p-4 mb-3" style={{ borderColor: "#DC2626" }} data-testid="meldungen-pane-error">
            <p className="text-sm font-semibold m-0" style={{ color: "#DC2626" }}>{error}</p>
          </div>
        )}
        {loading && !data && <p className="text-sm" style={{ color: "#64748B" }} data-testid="messages-loading">Meldungen werden geladen…</p>}
        {data && messages.length === 0 && !error && (
          <div className="text-center py-12" data-testid="messages-empty-state">
            <Inbox size={30} className="mx-auto mb-2" style={{ color: "#94A3B8" }} />
            <p className="font-bold mb-1">Keine Meldungen</p>
            <p className="text-sm" style={{ color: "#64748B" }}>Für diese Standort liegen aktuell keine Meldungen vor.</p>
          </div>
        )}
        {data && messages.length > 0 && visible.length === 0 && !error && (
          <div className="text-center py-12" data-testid="messages-filter-empty">
            <Inbox size={30} className="mx-auto mb-2" style={{ color: "#94A3B8" }} />
            <p className="font-bold mb-1">Keine Treffer</p>
            <p className="text-sm" style={{ color: "#64748B" }}>Für diesen Filter liegen keine Meldungen vor.</p>
          </div>
        )}

        <div className="flex flex-col gap-3">
          {visible.map((m) => {
            const done = m.status === "completed" || m.status === "external_service_required";
            return (
              <div key={m.id} data-testid={`meldung-item-${m.id}`} className="card-wd p-4"
                style={{ border: "1px solid #E2E8F0", opacity: done ? 0.85 : 1 }}>
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-xs font-bold" style={{ color: "#94A3B8" }}>{m.referenceNumber}</span>
                  {m.sourceTitle && (
                    <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-md" style={{ background: "#EFF6FF", color: "#1D4ED8" }} data-testid={`meldung-source-${m.id}`}>{m.sourceTitle}</span>
                  )}
                  {!m.mappable && (
                    <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-md" style={{ background: "#FEF2F2", color: "#B91C1C" }} data-testid={`meldung-unmappable-${m.id}`}><AlertTriangle size={12} /> unvollständig</span>
                  )}
                  {m.hasAttachments && (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md" style={{ background: "#F1F5F9", color: "#475569" }}><Paperclip size={12} /> Anhang</span>
                  )}
                  {involvesActive(m) && m.status !== "completed" && (
                    <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-md" style={{ background: "#ECFDF5", color: "#047857" }} data-testid={`meldung-mine-${m.id}`}><ClipboardList size={12} /> Meine Aufgabe</span>
                  )}
                  {m.openQuestion && (
                    <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-md" style={{ background: "#FFF7ED", color: "#C2410C" }} data-testid={`meldung-open-question-${m.id}`}><HelpCircle size={12} /> Rückfrage offen</span>
                  )}
                </div>
                <div className="font-bold text-sm" data-testid={`meldung-title-${m.id}`}>{m.subject || m.title}</div>
                {m.description && m.subject && m.description !== m.subject && (
                  <div className="text-sm mt-0.5" style={{ color: "#334155", whiteSpace: "pre-wrap", wordBreak: "break-word" }} data-testid={`meldung-desc-${m.id}`}>{m.description}</div>
                )}
                <div className="flex items-center gap-4 mt-1 text-xs flex-wrap" style={{ color: "#64748B" }}>
                  <span className="inline-flex items-center gap-1"><User size={12} /> {m.reporterName}</span>
                  <span className="inline-flex items-center gap-1"><Clock size={12} /> {fmtTime(m.reportedAt)}</span>
                </div>
                {m.attachments?.length > 0 && (
                  <AttachmentGallery attachments={m.attachments} token={token} mid={m.id} />
                )}

                <div className="mt-3 pt-3" style={{ borderTop: "1px solid #F1F5F9" }}>
                  {editId !== m.id ? (
                    <>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span data-testid={`meldung-status-${m.id}`}><StatusBadge status={m.status} /></span>
                            {m.statusBy && (
                              <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: "#F1F5F9", color: "#475569" }} data-testid={`meldung-status-by-${m.id}`}>
                                <User size={12} /> {m.statusBy}
                              </span>
                            )}
                          </div>
                          {m.statusAt && (
                            <span className="text-xs" style={{ color: "#94A3B8" }} data-testid={`meldung-status-meta-${m.id}`}>{fmtTime(m.statusAt)}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {answerEntries(m).length > 0 && (
                            <button data-testid={`meldung-verlauf-toggle-${m.id}`} onClick={() => toggleTimeline(m)} className="btn-secondary" style={{ padding: "6px 12px", fontSize: 13 }}>
                              <MessageSquare size={14} /> Verlauf ({answerEntries(m).length})
                              {unreadCount(m) > 0 && (
                                <span data-testid={`meldung-verlauf-unread-${m.id}`} className="inline-flex items-center justify-center rounded-full font-extrabold" style={{ background: "#DC2626", color: "#fff", fontSize: 10, minWidth: 18, height: 16, padding: "0 5px", marginLeft: 2 }}>{unreadCount(m)} neu</span>
                              )}
                            </button>
                          )}
                          <button data-testid={`meldung-edit-toggle-${m.id}`} onClick={() => openEdit(m.id)} className="btn-secondary" style={{ padding: "6px 12px", fontSize: 13 }}>
                            <Pencil size={14} /> Bearbeiten
                          </button>
                        </div>
                      </div>
                      {openTimeline.has(m.id) && answerEntries(m).length > 0 && (
                        <div className="mt-3 pt-3" style={{ borderTop: "1px solid #F1F5F9" }} data-testid={`meldung-verlauf-${m.id}`}>
                          <Timeline entries={answerEntries(m)} token={token} />
                        </div>
                      )}
                    </>
                  ) : (
                    <div data-testid={`meldung-actions-${m.id}`}>
                      {callFor === m.id ? (
                        <div data-testid={`meldung-call-${m.id}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-bold inline-flex items-center gap-2" style={{ color: "#475569" }}><Phone size={16} color="#F97316" /> Manager anrufen</span>
                            <button data-testid={`meldung-call-close-${m.id}`} onClick={() => setCallFor(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8" }}><X size={16} /></button>
                          </div>
                          {callLoading ? (
                            <div className="flex items-center gap-2 text-sm py-2" style={{ color: "#64748B" }}><Loader2 size={16} className="animate-spin" /> Manager werden geladen …</div>
                          ) : callMgrs.length === 0 ? (
                            <p className="text-sm py-2 mb-0" style={{ color: "#64748B" }} data-testid={`meldung-call-empty-${m.id}`}>Kein Manager mit hinterlegter Telefonnummer für diesen Standort.</p>
                          ) : (
                            <div className="flex flex-col gap-2">
                              {callMgrs.map((mgr) => (
                                <a key={mgr.email} data-testid={`meldung-call-tel-${m.id}`} href={`tel:${(mgr.phone || "").replace(/[^0-9+]/g, "")}`}
                                  className="flex items-center justify-between gap-2 px-3 py-2 rounded-xl font-bold text-sm no-underline"
                                  style={{ border: "2px solid #E2E8F0", background: "#fff", color: "#0F172A" }}>
                                  <span className="inline-flex items-center gap-2 min-w-0"><User size={14} color="#64748B" /><span className="truncate">{mgr.name}</span></span>
                                  <span className="inline-flex items-center gap-1" style={{ color: "#F97316" }}><Phone size={14} /> {mgr.phone}</span>
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : replyFor !== m.id ? (
                        <>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-bold" style={{ color: "#64748B" }}>Aktion wählen</span>
                            <button data-testid={`meldung-edit-close-${m.id}`} onClick={() => setEditId(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8" }}><X size={16} /></button>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {ORDER.filter((key) => {
                              if (key === "delete") return role === "admin" || role === "manager";
                              return key === "question" || ACTIONS[key].status !== m.status;
                            }).map((key) => {
                              const cfg = ACTIONS[key];
                              const busy = sendingId === m.id && sendingKey === key;
                              const isDelete = key === "delete";
                              return (
                                <button key={key} data-testid={`meldung-action-${key}-${m.id}`} disabled={sendingId === m.id}
                                  onClick={() => (isDelete ? setConfirmDelete(m.id) : cfg.requiresText ? (setReplyFor(m.id), setReplyAction(key)) : doAction(m, key, ""))}
                                  className="flex items-center gap-2 px-3 py-2 rounded-xl font-bold text-sm text-left"
                                  style={{ border: `2px solid ${isDelete ? "#FECACA" : "#E2E8F0"}`, background: isDelete ? "#FEF2F2" : "#fff", color: isDelete ? "#B91C1C" : "#0F172A", cursor: busy ? "wait" : "pointer", opacity: sendingId === m.id && !busy ? 0.5 : 1 }}>
                                  {busy ? <Loader2 size={14} className="animate-spin" /> : isDelete ? <Trash2 size={14} /> : null} {cfg.label}
                                </button>
                              );
                            })}
                            <button data-testid={`meldung-action-call-${m.id}`} onClick={() => openCall(m)}
                              className="flex items-center gap-2 px-3 py-2 rounded-xl font-bold text-sm text-left"
                              style={{ border: "2px solid #E2E8F0", background: "#fff", color: "#0F172A", cursor: "pointer" }}>
                              <Phone size={14} color="#F97316" /> Anrufen
                            </button>
                          </div>
                        </>
                      ) : (
                        <div data-testid={`meldung-reply-${m.id}`}>
                          <div className="flex items-center gap-2 mb-2 text-sm font-bold" style={{ color: "#475569" }}>
                            {replyAction === "note" ? <StickyNote size={16} color="#F97316" /> : <HelpCircle size={16} color="#F97316" />}
                            {replyAction === "note" ? "Notiz hinzufügen" : "Rückfrage an Standortleitung"}
                          </div>
                          <div className="flex items-end gap-2">
                            <textarea data-testid={`meldung-reply-input-${m.id}`} rows={2} className="input-wd text-sm" style={{ flex: 1 }}
                              placeholder={(ACTIONS[replyAction] || ACTIONS.question).placeholder} value={replyText} onChange={(e) => setReplyText(e.target.value)} />
                            <button data-testid={`meldung-reply-send-${m.id}`} className="btn-primary" disabled={sendingId === m.id || !replyText.trim()}
                              onClick={() => doAction(m, replyAction, replyText)} style={{ padding: "10px 12px" }}>
                              {sendingId === m.id && sendingKey === replyAction ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                            </button>
                          </div>
                          <button data-testid={`meldung-reply-cancel-${m.id}`} onClick={() => { setReplyFor(null); setReplyText(""); setReplyAction("question"); }}
                            className="text-xs mt-2" style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8" }}>Abbrechen</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {confirmDelete && (() => {
        const dm = messages.find((x) => String(x.id) === String(confirmDelete));
        if (!dm) return null;
        const deleting = sendingId === dm.id && sendingKey === "delete";
        return (
          <div className="modal-overlay" data-testid="ticket-delete-modal" onClick={() => !deleting && setConfirmDelete(null)}>
            <div className="modal-card" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-head">
                <div className="flex items-center gap-2">
                  <span className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "#FEF2F2" }}>
                    <Trash2 size={18} color="#B91C1C" />
                  </span>
                  <h3 className="text-lg font-extrabold m-0">Ticket löschen?</h3>
                </div>
                <button className="modal-close" data-testid="ticket-delete-close" onClick={() => !deleting && setConfirmDelete(null)}><X size={20} /></button>
              </div>
              <div style={{ padding: 24 }}>
                <p className="text-sm m-0" style={{ color: "#334155" }}>
                  Dieses Ticket wird aus allen Listen entfernt und niemandem mehr angezeigt. Diese Aktion kann nicht rückgängig gemacht werden.
                </p>
                <div className="flex items-center justify-end gap-2 mt-5">
                  <button data-testid="ticket-delete-cancel" className="btn-secondary" disabled={deleting} onClick={() => setConfirmDelete(null)}>Abbrechen</button>
                  <button data-testid="ticket-delete-confirm" disabled={deleting} onClick={() => doAction(dm, "delete", "")}
                    className="inline-flex items-center gap-1.5 rounded-lg font-bold"
                    style={{ background: "#DC2626", color: "#fff", border: "none", padding: "10px 16px", cursor: deleting ? "wait" : "pointer" }}>
                    {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                    {deleting ? "Löschen…" : "Ja, löschen"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
